import { sectionTitle } from '../utils/ruleTextUtils';
import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Modal, Segmented, Skeleton } from 'antd';
import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { PREVIEW_DRIVER_ACTIVITY_RULE_TEXT, PreviewDriverActivityRuleTextResponse, PreviewDriverActivityRuleTextVariables } from '../graphql/queries';
import { DriverActivityRenderedSection, DriverActivityRuleInput, DriverActivityRuleTemplateSection, DriverActivityRuleTextInput } from '../graphql/types';
import { parseException } from '../../../../utils/exceptionUtils';

export interface RuleTextPreviewSource {
    startDate?: string | null;
    endDate?: string | null;
    maxRewardAmount?: string | null;
    rules: DriverActivityRuleInput[];
    ruleText: DriverActivityRuleTextInput[];
}

interface RuleTextPreviewModalProps {
    open: boolean;
    onClose: () => void;
    /** 当前表单 / 详情的值；未填的传空，后端把拿不到值的占位原样留着 */
    source: RuleTextPreviewSource;
    languages: string[];
    /** 模板段定义；传了之后，该语种没填的必填段也占位显示标题 + 「未填写」提示，不会悄悄消失（可选段空着仍不显示） */
    sections?: DriverActivityRuleTemplateSection[];
}

/** 一段正文：有 segments 按段渲染，变量替换出来的值加紫底；没有 segments 退回整段 content */
export const RenderedSectionBody: React.FC<{ section: DriverActivityRenderedSection }> = ({ section }) => {
    const segments = section.segments ?? [];
    if (!segments.length) {
        return <>{section.content || <span className="text-[#BFBFBF]">--</span>}</>;
    }
    return (
        <>
            {segments.map((segment, i) =>
                segment.variable ? (
                    <span
                        key={i}
                        className="bg-[#F3EEFE] text-[#722ed1] rounded px-[4px]"
                        data-testid={`preview-variable-${segment.variable}`}
                    >
                        {segment.text}
                    </span>
                ) : (
                    <React.Fragment key={i}>{segment.text}</React.Fragment>
                )
            )}
        </>
    );
};

/**
 * 规则文案预览：顶部切规则（规则 1 / 规则 2 …）和语种，内容调 previewDriverActivityRuleText（不落库），
 * 按司机 App 规则页的样子展示：每段一行加粗小标题 + 正文段落，变量替换出来的值带紫底。
 */
const RuleTextPreviewModal: React.FC<RuleTextPreviewModalProps> = ({ open, onClose, source, languages, sections: templateSections }) => {
    const { t, i18n } = useTranslation();
    const [ruleIndex, setRuleIndex] = useState(0);
    const [language, setLanguage] = useState(languages[0]);
    const ruleCount = Math.max(source.rules.length, 1);

    useEffect(() => {
        if (open) {
            setRuleIndex(0);
            setLanguage(languages[0]);
        }
    }, [open, languages]);

    const { data, loading, error } = useQuery<PreviewDriverActivityRuleTextResponse, PreviewDriverActivityRuleTextVariables>(
        PREVIEW_DRIVER_ACTIVITY_RULE_TEXT,
        {
            variables: { input: { ...source, ruleIndex, language } },
            skip: !open,
            fetchPolicy: 'network-only',
        }
    );
    const sections = data?.previewDriverActivityRuleText?.sections ?? [];
    // 段标题按预览语种取，和编辑/详情页一致
    const tl = useMemo(() => i18n.getFixedT(language), [i18n, language]);
    /** 展示清单：按模板顺序，已渲染的照常；必填却没渲染（该语种没填）的占位；可选段没填的跳过 */
    const displaySections = useMemo(() => {
        if (!templateSections?.length) return sections.map((section) => ({ key: section.key, title: section.title, section }));
        return templateSections
            .map((def) => {
                const section = sections.find((item) => item.key === def.key);
                if (section) return { key: def.key, title: section.title, section };
                if (!def.required) return null;
                // 编辑页的段标题 i18n 带尾部冒号，预览里和后端渲染的标题保持一致，去掉
                const title = sectionTitle(tl, def.key);
                return { key: def.key, title, section: null };
            })
            .filter((item): item is { key: string; title: string; section: DriverActivityRenderedSection | null } => item !== null);
    }, [templateSections, sections, tl]);

    const ruleOptions = Array.from({ length: ruleCount }, (_, i) => ({
        value: i,
        label: t('driverOps.activity.form.ruleNo', { no: i + 1 }),
    }));
    const langOptions = languages.map((code) => ({
        value: code,
        label: t(`driverOps.activity.languages.${code}`, { defaultValue: code }),
    }));

    return (
        <Modal open={open} onCancel={onClose} footer={null} width={640} title={t('driverOps.activity.preview.title')} destroyOnClose>
            <div className="flex items-center gap-3 mb-3 flex-wrap">
                <Segmented size="small" options={ruleOptions} value={ruleIndex} onChange={(v) => setRuleIndex(Number(v))} />
                <Segmented size="small" options={langOptions} value={language} onChange={(v) => setLanguage(String(v))} />
            </div>
            {error && <Alert type="error" showIcon className="mb-3" message={parseException(error).message} />}
            {loading ? (
                <Skeleton active paragraph={{ rows: 6 }} />
            ) : (
                <div className="border border-[#EFEFEF] rounded-[6px] bg-[#FAFAFA] px-5 py-4 max-h-[60vh] overflow-y-auto">
                    {displaySections.map(({ key, title, section }) => (
                        <div key={key} className="mb-[14px] last:mb-0" data-testid={`preview-section-${key}`}>
                            <div className="font-semibold text-[14px] leading-[1.6] text-[#1F1F1F] mb-1 break-words">{title}</div>
                            <div className="text-[13.5px] leading-[1.8] text-[#1F1F1F] whitespace-pre-wrap break-words">
                                {section ? (
                                    <RenderedSectionBody section={section} />
                                ) : (
                                    <span className="text-[#F0453A]" data-testid={`preview-section-missing-${key}`}>
                                        {t('driverOps.activity.form.previewSectionMissing')}
                                    </span>
                                )}
                            </div>
                        </div>
                    ))}
                    {!displaySections.length && !error && <div className="p-4 text-center text-[#8C8C8C]">{t('common.noData')}</div>}
                </div>
            )}
        </Modal>
    );
};

export default RuleTextPreviewModal;
